import { User, MapPin, Calendar, Crown, Eye, Users } from 'lucide-react';
import { useAppStore } from '../../store/useAppStore';
import { useCountdown } from '../../hooks/useCountdown';

export const ProfileModal = () => {
  const { session } = useAppStore();
  const { timeLeft, isExpired } = useCountdown(session?.hy_sub_expire_date || '');

  if (!session) return null;

  const profileStats = [
    {
      icon: Eye,
      label: 'Views',
      value: session.hy_view,
      color: 'text-brand'
    },
    {
      icon: Users,
      label: 'User Count',
      value: session.hy_user_count,
      color: 'text-accent'
    },
    {
      icon: Calendar,
      label: 'Member Since',
      value: new Date(session.hy_sub_created_at).toLocaleDateString(),
      color: 'text-status-warning'
    }
  ];

  return (
    <div className="p-6 space-y-6">
      {/* Profile Header */}
      <div className="card p-6">
        <div className="flex items-center space-x-4 mb-6">
          <div className="w-16 h-16 bg-brand rounded-full flex items-center justify-center">
            <User className="w-8 h-8 text-white" />
          </div>
          <div>
            <h3 className="text-2xl font-bold text-th-primary">
              {session.hy_name}
            </h3>
            <div className="flex items-center space-x-2 text-th-secondary">
              <MapPin className="w-4 h-4" />
              <span>{session.hy_country} ({session.hy_country_code})</span>
            </div>
          </div>
        </div>

        {/* Plan Info */}
        <div className="flex items-center justify-between p-4 bg-surface rounded-xl">
          <div className="flex items-center space-x-2">
            <Crown className="w-5 h-5 text-status-warning" />
            <div>
              <p className="text-th-primary font-medium">
                {session.hy_plan_dev_name}
              </p>
              <p className="text-th-secondary text-sm">
                SKU: {session.hy_sku}
              </p>
            </div>
          </div>
          <div className="text-right">
            <p className={`text-sm font-medium ${isExpired ? 'text-status-error' : 'text-status-success'}`}>
              {isExpired ? 'Expired' : 'Active'}
            </p>
            <p className="text-th-secondary text-xs">
              {timeLeft}
            </p>
          </div>
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {profileStats.map((stat, _) => (
          <div key={stat.label} className="card p-4">
            <div className="flex items-center space-x-3">
              <stat.icon className={`w-6 h-6 ${stat.color}`} />
              <div>
                <p className="text-th-secondary text-sm">
                  {stat.label}
                </p>
                <p className="text-th-primary text-lg font-semibold">
                  {stat.value}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Account Details */}
      <div className="bg-elevated border border-subtle rounded-xl p-6">
        <h4 className="text-lg font-semibold text-th-primary mb-4">
          Account Details
        </h4>
        <div className="space-y-3">
          <div className="flex justify-between">
            <span className="text-th-secondary">User ID</span>
            <span className="text-th-primary font-mono text-sm">
              {session.hy_user_id}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-th-secondary">Subscription ID</span>
            <span className="text-th-primary font-mono text-sm">
              {session.hy_sub_id}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-th-secondary">Plan Type</span>
            <span className="text-th-primary">
              {session.hy_plan_one_time ? 'One-time' : 'Recurring'}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-th-secondary">Last Synced</span>
            <span className="text-th-primary">
              {new Date(session.synced_at).toLocaleString()}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
